'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useRouter } from '@/shared/navigation'
import { startTrip } from '@/features/delman/api/startTrip'
import { completeTrip } from '@/features/delman/api/completeTrip'
import { DelmanBottomSheet, DelmanSheetSummary } from '@/features/delman/components/DelmanBottomSheet'
import { DelmanPwaInstallBanner } from '@/features/delman/components/DelmanPwaInstallBanner'
import { loadTodaySessions } from '@/features/delman/api/delmanTripApi'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  estimateTransitRemainder,
  getOrCreateTodayTrip,
  getTripSummary,
  getTripTotalShippedPcs,
  isStopInProgress,
  isYesterdayDate,
  loadLastCompletedTrip,
  saveLastCompletedTrip,
  saveTripRecord,
  sortStopsForPicker,
  stopActionLabel,
  stopBadgeClass,
  stopItineraryMeta,
  stopStatusLabel,
  type DeliveryStop,
  type TripRecord,
} from '@/features/delman/storage/tripStorage'
import { isDelmanCheckedInToday } from '@/features/delman/storage/delmanAttendanceStorage'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { Toast } from '@/shared/components/Toast'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { useReceivingPoll } from '@/features/delman/hooks/useReceivingPoll'

function TripSummaryStrip({
  trip,
  summary,
  totalPcs,
  pendingReturns,
}: {
  trip: TripRecord
  summary: ReturnType<typeof getTripSummary>
  totalPcs: number
  pendingReturns: number
}) {
  const pct =
    summary.totalStops > 0 ? Math.round((summary.completedStops / summary.totalStops) * 100) : 0

  const startedStoreName = trip.startedViaStopId
    ? trip.stops.find((s) => s.id === trip.startedViaStopId)?.storeName
    : undefined

  const subtitle =
    trip.status === 'ready'
      ? delmanTerms.trip.summaryStripReady
      : startedStoreName
        ? delmanTerms.trip.summaryStartedFrom(
            startedStoreName,
            summary.completedStops,
            summary.totalStops,
          )
        : delmanTerms.trip.progress(summary.completedStops, summary.totalStops)

  return (
    <div className="flex items-center gap-3 rounded-lg border border-outline-variant/60 bg-surface-container/50 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-semibold text-on-surface">
          {summary.totalStops} toko · {totalPcs} pcs
          {pendingReturns > 0 && (
            <span className="text-status-warning font-medium"> · {pendingReturns} retur</span>
          )}
        </p>
        <p className="text-[11px] text-on-surface-variant mt-0.5">{subtitle}</p>
      </div>
      {trip.status === 'in_progress' && summary.totalStops > 0 && (
        <div className="shrink-0 w-12" aria-hidden>
          <div className="h-1.5 rounded-full bg-surface-container overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] font-mono text-on-surface-variant text-right mt-0.5">{pct}%</p>
        </div>
      )}
    </div>
  )
}

function stopTotalPcs(stop: DeliveryStop): number {
  return stop.lines.reduce((sum, line) => sum + line.qtyShipped, 0)
}

function ChecklistRow({
  done,
  label,
  action,
}: {
  done: boolean
  label: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-start gap-2.5">
      <span
        className={`material-symbols-outlined text-xl shrink-0 ${
          done ? 'text-status-success icon-fill' : 'text-on-surface-variant'
        }`}
      >
        {done ? 'check_circle' : 'radio_button_unchecked'}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-body-sm ${done ? 'text-on-surface font-medium' : 'text-on-surface-variant'}`}>
          {label}
        </p>
        {action}
      </div>
    </div>
  )
}

function TripCompleteChecklist({
  summary,
  canComplete,
  onCompleteClick,
}: {
  summary: ReturnType<typeof getTripSummary>
  canComplete: boolean
  onCompleteClick: () => void
}) {
  const returnsDone = summary.pendingReturns === 0

  return (
    <div className="card space-y-3 border-outline-variant">
      <p className="text-body-sm font-semibold text-on-surface">{delmanTerms.trip.completeChecklistTitle}</p>
      <div className="space-y-2.5">
        <ChecklistRow
          done={summary.allDeliveriesDone}
          label={delmanTerms.trip.completeCheckDelivery(summary.completedStops, summary.totalStops)}
        />
        <ChecklistRow
          done={returnsDone}
          label={delmanTerms.trip.completeCheckReturns(summary.pickedReturns, summary.pendingReturns)}
          action={
            !returnsDone ? (
              <Link
                href="/delman/pickup-retur"
                className="text-label-md text-primary font-semibold mt-1 inline-flex items-center gap-0.5"
              >
                {delmanTerms.trip.completeCheckReturnsAction}
                <span className="material-symbols-outlined text-base">chevron_right</span>
              </Link>
            ) : undefined
          }
        />
      </div>
      <p className="text-label-md text-on-surface-variant">{delmanTerms.trip.completeHint}</p>
      <button
        type="button"
        className="btn-outline w-full min-h-[48px] disabled:opacity-40"
        disabled={!canComplete}
        onClick={onCompleteClick}
      >
        {delmanTerms.trip.complete}
      </button>
    </div>
  )
}

function YesterdayTripCard({ trip }: { trip: TripRecord }) {
  return (
    <div className="card bg-surface-container/40 border-outline-variant/80">
      <p className="text-label-md font-semibold text-on-surface-variant uppercase tracking-wide text-[10px]">
        {delmanTerms.trip.yesterdayTripTitle}
      </p>
      <p className="text-body-sm text-on-surface mt-1 font-mono">
        {delmanTerms.trip.yesterdayTripSummary(trip.id, trip.stops.length)}
      </p>
      {trip.transitRemainderQty != null && (
        <p className="text-label-md text-on-surface-variant mt-1">
          {delmanTerms.trip.closedRemainder(trip.transitRemainderQty)}
        </p>
      )}
    </div>
  )
}

function CompletedSessionsTodayCard({ sessions }: { sessions: TripRecord[] }) {
  if (sessions.length === 0) return null

  return (
    <div className="card bg-surface-container/40 border-outline-variant/80 space-y-2">
      <p className="text-label-md font-semibold text-on-surface-variant uppercase tracking-wide text-[10px]">
        {delmanTerms.trip.sessionHistoryTitle}
      </p>
      <ul className="space-y-1.5">
        {sessions.map((session, index) => (
          <li
            key={session.id}
            className="flex items-center gap-2 text-body-sm text-on-surface"
          >
            <span className="material-symbols-outlined text-status-success text-lg icon-fill shrink-0">
              check_circle
            </span>
            <span className="font-mono text-[13px]">
              {delmanTerms.trip.sessionHistoryItem(index + 1, session.stops.length)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function BetweenSessionsBanner() {
  return (
    <div className="card border-status-success/30 bg-status-success/5">
      <div className="flex items-start gap-2">
        <span className="material-symbols-outlined text-status-success icon-fill shrink-0">task_alt</span>
        <p className="text-body-sm text-on-surface">{delmanTerms.trip.betweenSessionsHint}</p>
      </div>
    </div>
  )
}

function StoreStopCard({
  stop,
  tripStatus,
  startedViaStopId,
  interactive,
  onOpen,
}: {
  stop: DeliveryStop
  tripStatus: TripRecord['status']
  startedViaStopId?: string
  interactive: boolean
  onOpen?: (stop: DeliveryStop) => void
}) {
  const meta = stopItineraryMeta(stop)
  const totalPcs = stopTotalPcs(stop)
  const isDone = stop.receivingStatus === 'completed'
  const inProgress = tripStatus === 'in_progress' && isStopInProgress(stop)
  const isTripStartStore =
    tripStatus === 'in_progress' && startedViaStopId != null && stop.id === startedViaStopId
  const actionLabel = stopActionLabel(stop, tripStatus)

  const cardClass = `${meta.lineClass} ${isDone ? 'opacity-75' : ''} ${
    isTripStartStore ? 'border-l-4 border-l-primary ring-1 ring-primary/20' : ''
  }`

  const content = (
    <div className="flex items-center gap-3 w-full">
      <span
        className={`material-symbols-outlined shrink-0 p-2.5 rounded-full flex items-center justify-center leading-none ${meta.iconClass}`}
        aria-hidden
      >
        {meta.icon}
      </span>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="text-body-sm font-semibold text-on-surface">{stop.storeName}</p>
          {isTripStartStore && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-primary text-on-primary">
              <span className="material-symbols-outlined text-[11px]">flag</span>
              {delmanTerms.trip.storeStartedHere}
            </span>
          )}
          {inProgress && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-status-warning-bg text-status-warning border border-status-warning/30">
              <span className="material-symbols-outlined text-[11px]">hourglass_top</span>
              {delmanTerms.trip.storeInProgress}
            </span>
          )}
        </div>
        <p className="text-label-md text-on-surface-variant mt-0.5">{totalPcs} pcs</p>
        {interactive && (
          <p className="text-label-md text-primary font-semibold mt-1">{actionLabel}</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className={`${stopBadgeClass(stop)} max-w-[108px] text-center leading-tight text-[10px]`}>
          {stopStatusLabel(stop)}
        </span>
        {interactive && (
          <span className="material-symbols-outlined text-primary text-lg">chevron_right</span>
        )}
      </div>
    </div>
  )

  if (!interactive || !onOpen) {
    return (
      <div className={`rounded-xl border p-3 min-h-[56px] ${cardClass}`}>{content}</div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(stop)}
      className={`w-full rounded-xl border p-3 min-h-[56px] transition-colors active:scale-[0.99] ${cardClass} ${
        isDone ? 'opacity-80' : 'hover:border-primary/50'
      }`}
    >
      {content}
    </button>
  )
}

function StorePickerList({
  stops,
  tripStatus,
  startedViaStopId,
  interactive,
  onOpen,
  hint,
}: {
  stops: DeliveryStop[]
  tripStatus: TripRecord['status']
  startedViaStopId?: string
  interactive: boolean
  onOpen?: (stop: DeliveryStop) => void
  hint: string
}) {
  const sorted = useMemo(
    () => sortStopsForPicker(stops, startedViaStopId),
    [stops, startedViaStopId],
  )

  return (
    <div className="card space-y-3">
      <div>
        <p className="text-body-sm font-semibold text-on-surface">{delmanTerms.trip.itinerary}</p>
        <p className="text-label-md text-on-surface-variant mt-1">{hint}</p>
      </div>
      <div className="space-y-2">
        {sorted.map((stop) => (
          <StoreStopCard
            key={stop.id}
            stop={stop}
            tripStatus={tripStatus}
            startedViaStopId={startedViaStopId}
            interactive={interactive}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  )
}

export function DelmanTripPage() {
  const router = useRouter()
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const delmanName = device?.delmanName ?? 'Delman'
  const kitchenName = device?.kitchenName ?? 'Dapur'

  const [trip, setTrip] = useState<TripRecord>(() => getOrCreateTodayTrip(delmanName, kitchenName))
  const tripRef = useRef(trip)
  tripRef.current = trip
  const [isStarting, setIsStarting] = useState(false)
  const [startSheetOpen, setStartSheetOpen] = useState(false)
  const [pendingStop, setPendingStop] = useState<DeliveryStop | null>(null)
  const [completeSheetOpen, setCompleteSheetOpen] = useState(false)
  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [checkedIn, setCheckedIn] = useState(true)
  const [yesterdayTrip, setYesterdayTrip] = useState<TripRecord | null>(null)
  const [completedToday, setCompletedToday] = useState<TripRecord[]>([])

  const summary = useMemo(() => getTripSummary(trip), [trip])
  const totalShippedPcs = useMemo(() => getTripTotalShippedPcs(trip), [trip])
  const transitEstimate = useMemo(() => estimateTransitRemainder(trip), [trip])

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  useEffect(() => {
    if (!hydrated || !device) return
    const delmanId = device.delmanId
    const delmanName = device.delmanName
    const kitchenName = device.kitchenName

    setCheckedIn(isDelmanCheckedInToday(delmanId))
    const last = loadLastCompletedTrip(delmanName)
    setYesterdayTrip(last && isYesterdayDate(last.date) ? last : null)

    async function loadTrip() {
      if (shouldUseDelmanMock()) {
        const fallback = getOrCreateTodayTrip(delmanName, kitchenName)
        setTrip(fallback)
        setCompletedToday([])
        return
      }
      try {
        const sessions = await loadTodaySessions({
          delmanId,
          delmanName,
          kitchenName,
        })
        setTrip(sessions.active)
        setCompletedToday(sessions.completedToday)
      } catch {
        setTrip(getOrCreateTodayTrip(delmanName, kitchenName))
        setCompletedToday([])
      }
    }

    void loadTrip()

    function refreshTrip() {
      void loadTrip()
    }
    window.addEventListener('focus', refreshTrip)
    document.addEventListener('visibilitychange', refreshTrip)
    return () => {
      window.removeEventListener('focus', refreshTrip)
      document.removeEventListener('visibilitychange', refreshTrip)
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  useEffect(() => {
    if (!hydrated || !device) return
    const delmanId = device.delmanId
    function refreshAttendance() {
      setCheckedIn(isDelmanCheckedInToday(delmanId))
    }
    window.addEventListener('focus', refreshAttendance)
    document.addEventListener('visibilitychange', refreshAttendance)
    return () => {
      window.removeEventListener('focus', refreshAttendance)
      document.removeEventListener('visibilitychange', refreshAttendance)
    }
  }, [hydrated, device?.delmanId])

  function navigateToStop(stop: DeliveryStop) {
    router.push(`/delman/delivery/${stop.id}`)
  }

  function openStore(stop: DeliveryStop) {
    if (trip.status === 'ready') {
      if (!checkedIn) {
        showToast(delmanTerms.trip.attendanceRequired)
        return
      }
      setPendingStop(stop)
      setStartSheetOpen(true)
      return
    }
    if (trip.status !== 'in_progress') return
    navigateToStop(stop)
  }

  function persistTrip(next: TripRecord) {
    saveTripRecord(next)
    setTrip(next)
  }

  useReceivingPoll({
    enabled: hydrated && trip.status === 'in_progress',
    getTrip: () => tripRef.current,
    onTripUpdate: persistTrip,
    onStoreScanned: (event) => {
      showToast(delmanTerms.handover.storeScannedToast(event.storeName))
    },
    onStoreCompleted: (event) => {
      showToast(delmanTerms.handover.storeCompletedToast(event.storeName))
    },
  })

  function closeStartSheet() {
    setStartSheetOpen(false)
    setPendingStop(null)
  }

  async function handleStartTrip() {
    if (!device || trip.status !== 'ready' || !pendingStop) return
    const selectedStore = pendingStop.storeName
    setIsStarting(true)
    try {
      await startTrip({ tripId: trip.id, deviceId: device.deviceId })
      const queued = consumeLastSyncQueued() ? ` ${delmanTerms.sync.queued}` : ''
      persistTrip({
        ...trip,
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        startedViaStopId: pendingStop.id,
      })
      setStartSheetOpen(false)
      setPendingStop(null)
      showToast(`${delmanTerms.trip.startedToast(selectedStore)}${queued}`)
    } finally {
      setIsStarting(false)
    }
  }

  async function handleCompleteTrip(pin: string) {
    if (!device) throw new Error('Perangkat tidak terdaftar.')
    const result = await completeTrip({
      tripId: trip.id,
      deviceId: device.deviceId,
      pin,
    })
    const queued = consumeLastSyncQueued() ? ` ${delmanTerms.sync.queued}` : ''
    const completedTrip: TripRecord = {
      ...trip,
      status: 'completed',
      completedAt: result.completed_at,
      transitRemainderQty: result.transit_remainder_qty,
    }
    saveLastCompletedTrip(completedTrip)
    const sessions = await loadTodaySessions({
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
    })
    setCompletedToday(sessions.completedToday)
    persistTrip(sessions.active)
    setPinOpen(false)
    showToast(`${delmanTerms.trip.completedToast(result.transit_remainder_qty)}${queued}`)
  }

  const canComplete =
    trip.status === 'in_progress' &&
    summary.allDeliveriesDone &&
    summary.pendingReturns === 0

  const waitingBetweenSessions =
    trip.status === 'ready' && trip.stops.length === 0 && completedToday.length > 0

  return (
    <section className="space-y-3">
      <DelmanPwaInstallBanner />

      {trip.status !== 'completed' && (
        <TripSummaryStrip
          trip={trip}
          summary={summary}
          totalPcs={totalShippedPcs}
          pendingReturns={summary.pendingReturns}
        />
      )}

      {trip.status === 'ready' && (
        <>
          {!checkedIn && (
            <Link
              href="/delman/absensi"
              className="card block border-status-warning/40 bg-status-warning-bg/30 hover:border-status-warning/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-status-warning text-2xl">badge</span>
                <div className="min-w-0 flex-1">
                  <p className="text-body-sm font-semibold text-status-warning">
                    {delmanTerms.trip.attendanceRequiredBanner}
                  </p>
                  <p className="text-label-md text-on-surface-variant mt-1">
                    {delmanTerms.trip.attendanceRequiredAction}
                  </p>
                </div>
                <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
              </div>
            </Link>
          )}

          {yesterdayTrip && <YesterdayTripCard trip={yesterdayTrip} />}

          {completedToday.length > 0 && <CompletedSessionsTodayCard sessions={completedToday} />}

          {waitingBetweenSessions && <BetweenSessionsBanner />}

          {trip.stops.length === 0 ? (
            <div className="card">
              <div className="flex items-start gap-2 rounded-lg bg-status-warning-bg/40 border border-status-warning/30 px-3 py-3">
                <span className="material-symbols-outlined text-status-warning shrink-0">info</span>
                <p className="text-body-sm text-status-warning">
                  {waitingBetweenSessions
                    ? delmanTerms.trip.waitingNextDispatch
                    : delmanTerms.trip.emptyStops}
                </p>
              </div>
            </div>
          ) : (
            <StorePickerList
              stops={trip.stops}
              tripStatus={trip.status}
              startedViaStopId={trip.startedViaStopId}
              interactive
              onOpen={openStore}
              hint={delmanTerms.trip.storePickerReadyHint}
            />
          )}
        </>
      )}

      {trip.status === 'in_progress' && (
        <>
          <StorePickerList
            stops={trip.stops}
            tripStatus={trip.status}
            startedViaStopId={trip.startedViaStopId}
            interactive
            onOpen={openStore}
            hint={delmanTerms.trip.storePickerHint}
          />

          {summary.pendingReturns > 0 && (
            <Link
              href="/delman/pickup-retur"
              className="card block border-status-warning/40 bg-status-warning-bg/30 hover:border-status-warning/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-status-warning text-2xl">qr_code_scanner</span>
                <div className="min-w-0 flex-1">
                  <p className="text-body-sm font-semibold text-status-warning">
                    {delmanTerms.trip.returnBanner(summary.pendingReturns)}
                  </p>
                  <p className="text-label-md text-on-surface-variant mt-1">{delmanTerms.trip.returnBannerAction}</p>
                </div>
                <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
              </div>
            </Link>
          )}

          <TripCompleteChecklist
            summary={summary}
            canComplete={canComplete}
            onCompleteClick={() => setCompleteSheetOpen(true)}
          />
        </>
      )}

      {trip.status === 'completed' && (
        <div className="card space-y-3 border-status-success/30 bg-status-success/5">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-status-success icon-fill">task_alt</span>
            <p className="text-body-sm text-status-success font-semibold">{delmanTerms.trip.closedTitle}</p>
          </div>
          {trip.transitRemainderQty != null && (
            <p className="text-body-sm text-on-surface-variant">
              {delmanTerms.trip.closedRemainder(trip.transitRemainderQty)}
            </p>
          )}
          <p className="text-label-md text-on-surface-variant">{delmanTerms.trip.closedFooter}</p>
          <p className="text-body-sm text-on-surface">{delmanTerms.trip.betweenSessionsHint}</p>
        </div>
      )}

      <DelmanBottomSheet
        open={startSheetOpen && pendingStop !== null}
        title={delmanTerms.trip.startConfirmTitle}
        onClose={closeStartSheet}
        primaryLabel={delmanTerms.trip.startConfirmPrimary}
        onPrimary={() => void handleStartTrip()}
        primaryLoading={isStarting}
      >
        {pendingStop && (
          <>
            <p className="text-body-sm text-on-surface-variant mb-4">
              {delmanTerms.trip.startConfirmWithStore(pendingStop.storeName)}
            </p>
            <DelmanSheetSummary
              rows={[
                { label: 'ID trip', value: trip.id },
                { label: 'Toko dimulai', value: pendingStop.storeName, highlight: true },
                { label: 'Jumlah toko', value: String(summary.totalStops), highlight: true },
                { label: 'Total barang', value: `${totalShippedPcs} pcs`, highlight: true },
              ]}
            />
          </>
        )}
      </DelmanBottomSheet>

      <DelmanBottomSheet
        open={completeSheetOpen}
        title={`${delmanTerms.trip.completeSheetTitle} ${trip.id}`}
        onClose={() => setCompleteSheetOpen(false)}
        primaryLabel={delmanTerms.trip.completeSheetPrimary}
        onPrimary={() => {
          setCompleteSheetOpen(false)
          setPinOpen(true)
        }}
      >
        <p className="text-body-sm text-on-surface-variant mb-4">{delmanTerms.trip.completeHint}</p>
        <DelmanSheetSummary
          rows={[
            {
              label: delmanTerms.trip.completeSheetDelivery,
              value: delmanTerms.trip.progress(summary.completedStops, summary.totalStops),
              highlight: true,
            },
            {
              label: delmanTerms.trip.completeSheetReturns,
              value: `${summary.pickedReturns} dokumen`,
              highlight: true,
            },
            {
              label: delmanTerms.trip.completeSheetTransit,
              value: delmanTerms.trip.completeSheetMockTransit(transitEstimate),
            },
          ]}
        />
        <p className="text-label-md text-on-surface-variant mt-3 text-center">
          {delmanTerms.trip.completeSheetTransitHint}
        </p>
      </DelmanBottomSheet>

      <PinKeypadModal
        open={pinOpen}
        title="PIN konfirmasi"
        description="Masukkan PIN untuk menyelesaikan trip hari ini."
        confirmLabel={delmanTerms.trip.complete}
        onClose={() => setPinOpen(false)}
        onConfirm={handleCompleteTrip}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
